import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Send } from "lucide-react";
import { toast } from "sonner";

const bubble = {
  user: "bg-[#2A3B32] text-[#FDFBF7] rounded-br-sm",
  coach: "bg-[#F2EBE5] text-[#1C221D] rounded-bl-sm",
  india: "bg-[#F2CC8F]/50 text-[#1C221D] rounded-bl-sm",
};
const label = { user: "Client", coach: "AI (as you)", india: "You" };

export const CoachConversation = ({ sessionId }) => {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.get(`/admin/sessions/${sessionId}`).then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    setData(null);
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const toggleAi = async (paused) => {
    try {
      const r = await api.patch(`/admin/sessions/${sessionId}`, { ai_paused: paused });
      setData((d) => ({ ...d, session: r.data }));
      toast.success(paused ? "AI paused. Only you will reply here." : "AI replies turned back on");
    } catch {
      toast.error("Could not change AI setting");
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/sessions/${sessionId}/reply`, { content: reply });
      setReply("");
      load();
    } catch {
      toast.error("Could not send reply");
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="p-10 text-[#5C6A61]">Loading…</div>;
  const { session, client, messages, goals } = data;

  return (
    <div className="bg-white rounded-3xl border border-[#2A3B32]/10 flex flex-col min-h-[75vh] overflow-hidden" data-testid="coach-conversation">
      <div className="px-6 py-5 border-b border-[#2A3B32]/8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-serif-display text-2xl text-[#1C221D]" data-testid="coach-client-name">{client?.name}</div>
          <div className="text-xs text-[#5C6A61]">{client?.email} · {session.category}</div>
        </div>
        <label className="flex items-center gap-3 text-sm text-[#1C221D]">
          Pause AI, I'll reply myself
          <Switch checked={session.ai_paused} onCheckedChange={toggleAi} data-testid="coach-ai-pause-toggle" />
        </label>
      </div>

      {goals.length > 0 && (
        <div className="px-6 py-3 border-b border-[#2A3B32]/8 flex flex-wrap gap-2" data-testid="coach-client-goals">
          {goals.map((g) => (
            <span key={g.id} className="text-xs px-3 py-1 rounded-full bg-[#FDFBF7] border border-[#2A3B32]/10 text-[#1C221D]">
              {g.title} · {g.progress}%
            </span>
          ))}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-6 py-6 space-y-4">
        {messages.map((m) => (
          <div key={m.id} className={`flex flex-col ${m.role === "user" ? "items-start" : "items-end"}`}>
            <span className="eyebrow text-[#5C6A61] mb-1">{label[m.role]}</span>
            <div className={`rounded-2xl px-5 py-3 max-w-[85%] leading-relaxed whitespace-pre-wrap ${bubble[m.role]}`}>
              {m.content}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-[#2A3B32]/8 p-4 bg-[#FDFBF7] flex items-end gap-3">
        <textarea
          rows={2}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write a personal reply…"
          data-testid="coach-reply-input"
          className="flex-1 resize-none px-4 py-3 rounded-2xl border border-[#2A3B32]/15 bg-white focus:outline-none focus:border-[#2A3B32]"
        />
        <button
          onClick={send}
          disabled={!reply.trim() || sending}
          data-testid="coach-reply-send"
          className="h-12 w-12 shrink-0 rounded-full bg-[#2A3B32] text-[#FDFBF7] flex items-center justify-center hover:-translate-y-0.5 active:scale-95 transition-transform duration-200 disabled:opacity-50"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default CoachConversation;
